import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Smartphone, QrCode, RefreshCw, CheckCircle2, XCircle, 
  AlertCircle, ArrowLeft, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import QRCode from 'qrcode';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// BankID status messages in Swedish
const HINT_MESSAGES = {
  outstandingTransaction: 'Starta BankID-appen...',
  noClient: 'Starta BankID-appen pa din enhet',
  started: 'Soker efter BankID...',
  userSign: 'Skriv in din sakerherskod i BankID-appen',
  userCancel: 'Autentiseringen avbrots',
  expiredTransaction: 'Tidsgrans for BankID overskreds',
  certificateErr: 'Certifikatfel',
  userMrtd: 'Kontrollera att du har ett giltigt BankID',
  cancelled: 'Autentiseringen avbrots'
};

export default function BankIDLogin({ onBack, onSuccess }) {
  const navigate = useNavigate();
  const [step, setStep] = useState('input'); // input, waiting, success, error
  const [personalNumber, setPersonalNumber] = useState('');
  const [orderRef, setOrderRef] = useState(null);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [qrCodeImage, setQrCodeImage] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [error, setError] = useState(null);
  const pollIntervalRef = useRef(null);
  const qrIntervalRef = useRef(null);

  // Generate QR code image
  const generateQRImage = useCallback(async (data) => {
    try {
      const qrImage = await QRCode.toDataURL(data, {
        width: 200,
        margin: 2,
        color: { dark: '#1e3a5f', light: '#ffffff' }
      });
      setQrCodeImage(qrImage);
    } catch (err) {
      console.error('QR generation error:', err);
    }
  }, []);

  // Poll for QR code updates
  const pollQRCode = useCallback(async (ref) => {
    try {
      const response = await fetch(`${API_BASE}/api/bankid/auth/qr/${ref}`);
      if (response.ok) {
        const data = await response.json();
        setQrCodeData(data.qr_code_data);
        generateQRImage(data.qr_code_data);
      }
    } catch (err) {
      console.error('QR poll error:', err);
    }
  }, [generateQRImage]);

  // Poll for authentication status
  const pollStatus = useCallback(async (ref) => {
    try {
      const response = await fetch(`${API_BASE}/api/bankid/login/collect/${ref}`);
      const data = await response.json();

      if (data.status === 'complete') {
        // Success! Store token and redirect
        clearInterval(pollIntervalRef.current);
        clearInterval(qrIntervalRef.current);
        
        if (data.token) {
          localStorage.setItem('token', data.token);
          setStep('success');
          setStatusMessage('Inloggning lyckades!');
          
          setTimeout(() => {
            if (onSuccess) {
              onSuccess(data.token);
            } else {
              navigate('/');
            }
          }, 1500);
        }
      } else if (data.status === 'failed') {
        clearInterval(pollIntervalRef.current);
        clearInterval(qrIntervalRef.current);
        setStep('error');
        setError(HINT_MESSAGES[data.hint_code] || 'Autentiseringen misslyckades');
      } else {
        // Still pending
        setStatusMessage(HINT_MESSAGES[data.hint_code] || 'Vantar pa BankID...');
      }
    } catch (err) {
      console.error('Status poll error:', err);
    }
  }, [navigate, onSuccess]);

  // Initiate BankID login
  const initiateLogin = async () => {
    setError(null);
    setStep('waiting');
    setStatusMessage('Startar BankID...');

    try {
      const response = await fetch(`${API_BASE}/api/bankid/login/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          personal_number: personalNumber || null 
        })
      });

      const data = await response.json();

      if (!response.ok) {
        setStep('error');
        setError(data.detail || 'Kunde inte starta BankID');
        return;
      }

      setOrderRef(data.order_ref);
      setQrCodeData(data.qr_code_data);
      generateQRImage(data.qr_code_data);

      // Start polling for status
      pollIntervalRef.current = setInterval(() => {
        pollStatus(data.order_ref);
      }, 2000);

      // Start polling for QR code updates
      qrIntervalRef.current = setInterval(() => {
        pollQRCode(data.order_ref);
      }, 1000);

      // Open BankID app on mobile
      if (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        window.location.href = data.bankid_url;
      }

    } catch (err) {
      setStep('error');
      setError('Ett fel uppstod vid anslutning till BankID');
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (qrIntervalRef.current) clearInterval(qrIntervalRef.current);
    };
  }, []);

  // Cancel authentication
  const handleCancel = async () => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    if (qrIntervalRef.current) clearInterval(qrIntervalRef.current);
    
    if (orderRef) {
      try {
        await fetch(`${API_BASE}/api/bankid/auth/cancel/${orderRef}`, {
          method: 'POST'
        });
      } catch (err) {
        // Ignore cancel errors
      }
    }
    
    setStep('input');
    setOrderRef(null);
    setQrCodeData(null);
    setQrCodeImage(null);
    setStatusMessage('');
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-[#1e3a5f] to-[#2a4d7a] rounded-2xl flex items-center justify-center">
          <Smartphone className="w-8 h-8 text-white" />
        </div>
        <CardTitle>Logga in med BankID</CardTitle>
        <CardDescription>
          Anvand BankID-appen for sakrare inloggning
        </CardDescription>
      </CardHeader>

      <CardContent>
        {/* Input Step */}
        {step === 'input' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Personnummer (valfritt)
              </label>
              <Input
                type="text"
                placeholder="YYYYMMDDXXXX"
                value={personalNumber}
                onChange={(e) => setPersonalNumber(e.target.value)}
                maxLength={13}
              />
              <p className="text-xs text-gray-500 mt-1">
                Lamna tomt for att valja BankID i appen
              </p>
            </div>

            <Button 
              className="w-full bg-[#1e3a5f] hover:bg-[#2a4d7a]"
              onClick={initiateLogin}
              size="lg"
            >
              <Smartphone className="w-5 h-5 mr-2" />
              Oppna BankID
            </Button>

            {onBack && (
              <Button
                variant="ghost"
                className="w-full"
                onClick={onBack}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Tillbaka till e-postinloggning
              </Button>
            )}
          </div>
        )}

        {/* Waiting Step */}
        {step === 'waiting' && (
          <div className="space-y-6 text-center">
            {/* QR Code */}
            {qrCodeImage && (
              <div className="relative inline-block">
                <div className="p-4 bg-white rounded-xl shadow-lg border-2 border-gray-100">
                  <img 
                    src={qrCodeImage} 
                    alt="BankID QR Code" 
                    className="w-48 h-48"
                  />
                </div>
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-white px-3 py-1 rounded-full shadow text-xs text-gray-500">
                  Skanna med BankID-appen
                </div>
              </div>
            )}

            {/* Status */}
            <div className="flex items-center justify-center gap-2 text-gray-600">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>{statusMessage}</span>
            </div>

            {/* Instructions */}
            <div className="text-sm text-gray-500 space-y-2">
              <p><strong>Pa mobilen?</strong> BankID-appen oppnas automatiskt.</p>
              <p><strong>Pa datorn?</strong> Skanna QR-koden med BankID-appen pa din mobil.</p>
            </div>

            <Button
              variant="outline"
              onClick={handleCancel}
              className="mt-4"
            >
              Avbryt
            </Button>
          </div>
        )}

        {/* Success Step */}
        {step === 'success' && (
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-green-800">Inloggning lyckades!</h3>
              <p className="text-gray-500">Du omdirigeras...</p>
            </div>
          </div>
        )}

        {/* Error Step */}
        {step === 'error' && (
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-red-800">Nagot gick fel</h3>
              <p className="text-gray-600">{error}</p>
            </div>
            <Button
              onClick={() => setStep('input')}
              className="mt-4"
            >
              Forsok igen
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
